import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Plus, Eye, Printer, Pencil, Trash2, FileSpreadsheet, Ban, PackageCheck } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { TotalBar } from '../components/TotalBar';
import { susunTotalOrder } from '../lib/totalBar';
import { formatIDR, formatDate, PAY_STATUS, methodLabel, ringkasBulan, saringBulan, bulanLabel } from '../lib/format';
import { TombolWa } from '../components/KirimWa';
import CancelOrderDialog from '../components/CancelOrderDialog';
import { PickupDialog } from '../components/PickupDialog';
import { useAuth } from '../context/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { HapusCari } from '../components/HapusCari';

const FILTERS = [
  { v: '', l: 'Semua' },
  { v: 'out', l: 'Sedang disewa' },
  { v: 'due_today', l: 'Kembali hari ini' },
  { v: 'overdue', l: 'Telat' },
  { v: 'booked', l: 'Terjadwal' },
  { v: 'belum_lengkap', l: 'Belum lengkap' },
  { v: 'laundry', l: 'Di laundry' },
  { v: 'done', l: 'Selesai' },
  { v: 'cancelled', l: 'Dibatalkan' },
];

const PAY_FILTERS = [
  { v: '', l: 'Semua Bayar' },
  { v: 'belum_bayar', l: 'Belum Bayar' },
  { v: 'dp', l: 'DP' },
  { v: 'lunas', l: 'Lunas' },
];

const KET_CLS = {
  packing: 'bg-gold/20 text-gold-dark',
  kembali: 'bg-success/10 text-success',
  telat: 'bg-danger/10 text-danger',
  out: 'bg-brown/10 text-brown',
  today: 'bg-warning/15 text-[#8a5f1e]',
  batal: 'bg-danger/10 text-danger',
  belum_lengkap: 'bg-warning/20 text-[#8a5f1e]',
};

function PayCell({ tx }) {
  const ps = PAY_STATUS[tx.payment_status];
  const sum = tx.payment_summary || {};
  const dp = sum.dp;
  const lunas = sum.pelunasan || sum.last;
  return (
    <div>
      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${ps?.cls}`}>
        {tx.payment_status === 'belum_bayar' ? 'Belum Bayar' : ps?.label}
      </span>
      {tx.payment_status === 'dp' && dp && (
        <p className="text-[10px] text-mocha mt-0.5 whitespace-nowrap">
          {formatIDR(dp.amount)} • {methodLabel(dp.method)} • {formatDate(dp.date)}
        </p>
      )}
      {tx.payment_status === 'lunas' && lunas && (
        <p className="text-[10px] text-mocha mt-0.5 whitespace-nowrap">
          {methodLabel(lunas.method)} • {formatDate(lunas.date)}
        </p>
      )}
    </div>
  );
}

function ActionButtons({ t, mobile, navigate, settings, isOwner, onDelete, onCancel, onPickup }) {
  // Order yang barangnya masih ada di customer (Belum lengkap) tidak boleh diubah/dibatalkan.
  const terkunci = t.barang_status?.key === 'belum_lengkap';
  const bisaDiambil = t.status === 'active' && !t.picked_up && t.barang_status?.key === 'booked';
  return (
    <div className={`flex items-center ${mobile ? 'gap-1' : 'justify-end gap-1'}`}>
      {bisaDiambil && (
        <button
          data-testid={`pickup-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`}
          onClick={() => onPickup(t)}
          className="inline-flex items-center gap-1 rounded-lg bg-brown text-white px-2.5 py-1.5 text-[11px] font-bold hover:bg-brown-med whitespace-nowrap"
          title="Tandai barang sudah diambil customer"
        >
          <PackageCheck size={13} /> Tandai diambil
        </button>
      )}
      <button data-testid={`view-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`} onClick={() => navigate(`/transaksi/${t.id}`)} className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Lihat orderan">
        <Eye size={15} />
      </button>
      <TombolWa
        target={{ kind: 'order', id: t.id }}
        testid={`wa-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`}
        className="p-1.5 rounded-lg text-success hover:bg-success/10"
        title="Kirim pesan WhatsApp ke customer"
      />
      <button data-testid={`print-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`} onClick={() => navigate(`/nota/${t.id}`)} className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Cetak nota">
        <Printer size={15} />
      </button>
      {t.status !== 'cancelled' && !terkunci && (
        <button data-testid={`edit-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`} onClick={() => navigate(`/transaksi/${t.id}/edit`)} className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Ubah orderan">
          <Pencil size={15} />
        </button>
      )}
      {t.status !== 'cancelled' && !terkunci && (
        <button data-testid={`cancel-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`} onClick={() => onCancel(t)} className="p-1.5 rounded-lg text-warning hover:bg-warning/10" title="Batalkan orderan">
          <Ban size={15} />
        </button>
      )}
      {isOwner && (
        <button data-testid={`delete-tx${mobile ? '-mobile' : ''}-${t.transaction_number}`} onClick={() => onDelete(t)} className="p-1.5 rounded-lg text-danger hover:bg-danger/10" title="Hapus">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

export default function Transactions() {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';
  const [list, setList] = useState(null);
  const [barangFilter, setBarangFilter] = useState('');
  const [payFilter, setPayFilter] = useState('');
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [pickupFor, setPickupFor] = useState(null);
  const [rekapBatal, setRekapBatal] = useState(null);
  const [unpaidOpen, setUnpaidOpen] = useState(false);
  const [unpaid, setUnpaid] = useState(null);
  const [unpaidBulan, setUnpaidBulan] = useState('');   // '' = semua bulan
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/settings').then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (barangFilter) params.set('barang', barangFilter);
    if (payFilter) params.set('payment_status', payFilter);
    if (q) params.set('q', q);
    api.get(`/transactions?${params}`).then((r) => setList(r.data)).catch((err) => {
      setList([]);
      toast.error(apiError(err, 'Gagal memuat daftar order'));
    });
  }, [barangFilter, payFilter, q]);

  const openUnpaid = () => {
    setUnpaidOpen(true);
    api.get('/transactions').then((r) =>
      setUnpaid(r.data.filter((t) => t.remaining > 0 && t.status !== 'cancelled'))
    );
  };

  // Bulan yang dipilih tapi sudah tidak punya order belum lunas (mis. baru dilunasi) = kembali ke Semua.
  const bulanList = ringkasBulan(unpaid);
  const bulanAktif = bulanList.some((b) => b.key === unpaidBulan) ? unpaidBulan : '';
  const unpaidTampil = saringBulan(unpaid, bulanAktif);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Rekap pembatalan hanya diambil saat filter "Dibatalkan" dibuka.
  const loadRekapBatal = useCallback(() => {
    if (barangFilter !== 'cancelled') return;
    api.get('/cancellations')
      .then((r) => setRekapBatal(r.data))
      .catch(() => setRekapBatal(null));
  }, [barangFilter]);

  useEffect(() => { loadRekapBatal(); }, [loadRekapBatal]);

  const doExport = async () => {
    try {
      const r = await api.get('/export/transactions', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deyran-transaksi-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data order di-export ke Excel');
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success(`Order ${deleting.transaction_number} dihapus`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="transactions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Order Sewa</h1>
          {list && <p className="text-xs text-mocha mt-1">{list.length} order</p>}
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <button
              onClick={doExport}
              data-testid="export-excel-button"
              className="inline-flex items-center gap-2 rounded-xl bg-white border border-border text-brown px-4 py-2.5 text-sm font-semibold hover:bg-ivory transition-colors"
            >
              <FileSpreadsheet size={16} /> Export Excel
            </button>
          )}
          <Link
            to="/transaksi/baru"
            data-testid="new-transaction-button"
            className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
          >
            <Plus size={16} /> Order Baru
          </Link>
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
        <input
          data-testid="transaction-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari customer, no order, atau barang…"
          className="w-full rounded-xl border border-border bg-white pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
        />
        <HapusCari show={!!q} onClear={() => setQ('')} testid="transaction-search-clear" />
      </div>
      <div className="flex gap-1.5 flex-wrap" data-testid="barang-status-filters">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v || 'semua'}`}
            onClick={() => setBarangFilter(f.v)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              barangFilter === f.v ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="payment-status-filters">
        {PAY_FILTERS.map((f) => (
          <button
            key={f.v}
            data-testid={`pay-filter-${f.v || 'semua'}`}
            onClick={() => setPayFilter(f.v)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              payFilter === f.v ? 'bg-caramel text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'
            }`}
          >
            {f.l}
          </button>
        ))}
        <button
          data-testid="unpaid-list-button"
          onClick={openUnpaid}
          className="rounded-full px-4 py-2 text-xs font-bold bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
        >
          Belum Lunas
        </button>
      </div>

      {barangFilter === 'cancelled' && rekapBatal && (
        <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="rekap-batal">
          <h2 className="font-display text-lg text-ink">Rekap Order Dibatalkan</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="rounded-xl bg-ivory p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Jumlah Order</p>
              <p className="font-display text-xl text-ink" data-testid="rekap-batal-jumlah">{rekapBatal.summary?.jumlah || 0}</p>
            </div>
            <div className="rounded-xl bg-ivory p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">DP Hangus</p>
              <p className="font-display text-xl text-success" data-testid="rekap-batal-hangus">{formatIDR(rekapBatal.summary?.total_hangus || 0)}</p>
            </div>
            <div className="rounded-xl bg-ivory p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">DP Dikembalikan</p>
              <p className="font-display text-xl text-danger" data-testid="rekap-batal-kembali">{formatIDR(rekapBatal.summary?.total_dikembalikan || 0)}</p>
            </div>
            <div className="rounded-xl bg-ivory p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Nilai Order Hilang</p>
              <p className="font-display text-xl text-mocha" data-testid="rekap-batal-nilai">{formatIDR(rekapBatal.summary?.nilai_order || 0)}</p>
            </div>
          </div>

          {(rekapBatal.per_alasan || []).length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">Alasan terbanyak</p>
              <div className="flex gap-1.5 flex-wrap mt-1.5" data-testid="rekap-batal-alasan">
                {rekapBatal.per_alasan.map((a) => (
                  <span key={a.alasan} className="rounded-full bg-brown/10 text-brown px-3 py-1 text-[11px] font-semibold">
                    {a.alasan} · {a.jumlah}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(rekapBatal.per_bulan || []).length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs min-w-[420px]" data-testid="rekap-batal-bulan">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                    <th className="py-2 font-bold">Bulan</th>
                    <th className="py-2 font-bold text-center">Order</th>
                    <th className="py-2 font-bold text-right">DP Hangus</th>
                    <th className="py-2 font-bold text-right">Dikembalikan</th>
                  </tr>
                </thead>
                <tbody>
                  {rekapBatal.per_bulan.map((m) => (
                    <tr key={m.bulan} className="border-b border-border/60">
                      <td className="py-2 font-semibold text-ink">{m.bulan || '—'}</td>
                      <td className="py-2 text-center">{m.jumlah}</td>
                      <td className="py-2 text-right text-success font-semibold">{formatIDR(m.hangus)}</td>
                      <td className="py-2 text-right text-danger font-semibold">{formatIDR(m.dikembalikan)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!list ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat order…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-mocha py-10 text-center" data-testid="transactions-empty">Belum ada order.</p>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-2xl border border-border shadow-soft overflow-x-auto" data-testid="transactions-table">
            <table className="w-full text-sm min-w-[1050px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                  <th className="px-4 py-3 font-bold">No. Order</th>
                  <th className="px-4 py-3 font-bold">Status Pembayaran</th>
                  <th className="px-4 py-3 font-bold">Pelanggan</th>
                  <th className="px-4 py-3 font-bold">Tgl Pakai</th>
                  <th className="px-4 py-3 font-bold">Harus Kembali</th>
                  <th className="px-4 py-3 font-bold text-center">Pcs</th>
                  <th className="px-4 py-3 font-bold text-right">Total</th>
                  <th className="px-4 py-3 font-bold">Keterangan</th>
                  <th className="px-4 py-3 font-bold text-right">Sisa</th>
                  <th className="px-4 py-3 font-bold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const ket = t.keterangan || {};
                  return (
                    <tr key={t.id} data-testid={`transaction-row-${t.transaction_number}`} className="border-b border-border/60 hover:bg-ivory/60 transition-colors">
                      <td className="px-4 py-3 font-bold text-brown whitespace-nowrap">{t.transaction_number}</td>
                      <td className="px-4 py-3"><PayCell tx={t} /></td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-ink whitespace-nowrap">{t.customer_name}</p>
                        <p className="text-[11px] text-mocha truncate max-w-[180px]">{t.rows.map((r) => r.product_name).join(', ')}</p>
                      </td>
                      <td className="px-4 py-3 text-mocha whitespace-nowrap">{formatDate(t.use_date)}</td>
                      <td className="px-4 py-3 text-mocha whitespace-nowrap">{formatDate(t.return_date)}</td>
                      <td className="px-4 py-3 text-center font-semibold">{t.pcs}</td>
                      <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatIDR(t.total)}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`keterangan-pill-${t.transaction_number}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${KET_CLS[ket.key] || KET_CLS.out}`}>
                          {ket.label}
                        </span>
                        {ket.sub && <p className="text-[10px] text-mocha mt-0.5 whitespace-nowrap">{ket.sub}</p>}
                        {t.status === 'cancelled' && (
                          <p className="text-[10px] text-mocha mt-0.5" data-testid={`cancel-info-${t.transaction_number}`}>
                            {t.cancel_reason || 'tanpa alasan'}
                            {t.cancel_forfeited > 0 && ` • hangus ${formatIDR(t.cancel_forfeited)}`}
                            {t.cancel_refunded > 0 && ` • kembali ${formatIDR(t.cancel_refunded)}`}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {t.payment_status === 'lunas' ? (
                          <span className="text-success font-bold text-xs">Lunas</span>
                        ) : (
                          <span className="text-danger font-bold">{formatIDR(t.remaining)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><ActionButtons t={t} navigate={navigate} settings={settings} isOwner={isOwner} onDelete={setDeleting} onCancel={setCancelling} onPickup={setPickupFor} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {list.map((t) => {
              const ket = t.keterangan || {};
              const ps = PAY_STATUS[t.payment_status];
              return (
                <div key={t.id} data-testid={`transaction-card-${t.transaction_number}`} className="bg-white rounded-2xl border border-border shadow-soft p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-display font-bold text-brown">{t.transaction_number}</span>
                    <div className="flex gap-1.5 flex-wrap">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${ps?.cls}`}>{ps?.label}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${KET_CLS[ket.key] || KET_CLS.out}`}>{ket.label}</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-ink mt-1.5">{t.customer_name}</p>
                  <p className="text-xs text-mocha mt-0.5">
                    Pakai {formatDate(t.use_date)} • kembali {formatDate(t.return_date)} • {t.pcs} pcs
                    {ket.sub ? ` • ${ket.sub}` : ''}
                  </p>
                  {t.status === 'cancelled' && (
                    <p className="text-[11px] text-mocha mt-0.5" data-testid={`cancel-info-mobile-${t.transaction_number}`}>
                      {t.cancel_reason || 'tanpa alasan'}
                      {t.cancel_forfeited > 0 && ` • DP hangus ${formatIDR(t.cancel_forfeited)}`}
                      {t.cancel_refunded > 0 && ` • dikembalikan ${formatIDR(t.cancel_refunded)}`}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <p className="font-display text-lg text-ink">
                      {formatIDR(t.total)}
                      {t.payment_status === 'lunas'
                        ? <span className="text-[11px] text-success font-sans font-bold ml-2">Lunas</span>
                        : t.remaining > 0 && <span className="text-[11px] text-danger font-sans font-bold ml-2">sisa {formatIDR(t.remaining)}</span>}
                    </p>
                    <ActionButtons t={t} mobile navigate={navigate} settings={settings} isOwner={isOwner} onDelete={setDeleting} onCancel={setCancelling} onPickup={setPickupFor} />
                  </div>
                </div>
              );
            })}
          </div>
          {(() => {
            const to = susunTotalOrder(list, barangFilter);
            return <TotalBar testid="order-total" judul={to.judul} items={to.items} catatan={to.catatan} />;
          })()}
        </>
      )}

      <Sheet open={unpaidOpen} onOpenChange={setUnpaidOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-ivory max-h-[80vh] overflow-y-auto" data-testid="unpaid-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-brown-dark">Belum Lunas</SheetTitle>
          </SheetHeader>
          {!unpaid ? (
            <p className="text-sm text-mocha py-8 text-center">Memuat…</p>
          ) : unpaid.length === 0 ? (
            <p className="text-sm text-mocha py-8 text-center" data-testid="unpaid-empty">Semua order sudah lunas.</p>
          ) : (
            <div className="py-4 space-y-2.5" data-testid="unpaid-list">
              <div className="flex gap-1.5 flex-wrap" data-testid="unpaid-bulan-filters">
                <button
                  data-testid="unpaid-bulan-semua"
                  onClick={() => setUnpaidBulan('')}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                    !bulanAktif ? 'bg-brown text-white' : 'bg-white border border-border text-mocha'}`}
                >
                  Semua bulan · {unpaid.length}
                </button>
                {bulanList.map((b) => (
                  <button
                    key={b.key}
                    data-testid={`unpaid-bulan-${b.key}`}
                    onClick={() => setUnpaidBulan(b.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                      bulanAktif === b.key ? 'bg-brown text-white' : 'bg-white border border-border text-mocha'}`}
                  >
                    {b.label} · {b.jumlah}
                  </button>
                ))}
              </div>
              <p className="text-xs text-mocha font-semibold" data-testid="unpaid-ringkas">
                {unpaidTampil.length} order • total sisa {formatIDR(unpaidTampil.reduce((s, t) => s + t.remaining, 0))}
                {bulanAktif ? ` • pakai ${bulanLabel(bulanAktif)}` : ''}
              </p>
              {unpaidTampil.map((t) => (
                <div key={t.id} data-testid={`unpaid-row-${t.transaction_number}`} className="bg-white rounded-2xl border border-border p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink text-sm truncate">{t.customer_name}</p>
                    <p className="text-xs text-mocha">{t.transaction_number} • pakai {formatDate(t.use_date)}</p>
                    <p className="text-xs text-danger font-bold mt-0.5" data-testid={`unpaid-sisa-${t.transaction_number}`}>Sisa {formatIDR(t.remaining)}</p>
                  </div>
                  <button
                    data-testid={`unpaid-view-${t.transaction_number}`}
                    onClick={() => navigate(`/transaksi/${t.id}`)}
                    className="p-2.5 rounded-xl bg-brown/10 text-brown shrink-0"
                    title="Lihat detail order"
                  >
                    <Eye size={17} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <PickupDialog tx={pickupFor} open={!!pickupFor} onClose={() => setPickupFor(null)} onDone={() => load()} />

      <CancelOrderDialog
        tx={cancelling}
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onDone={() => { load(); loadRekapBatal(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent data-testid="delete-tx-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-danger">Hapus Order?</AlertDialogTitle>
            <AlertDialogDescription>
              Order {deleting?.transaction_number} ({deleting?.customer_name}) akan dihapus permanen beserta riwayat pembayarannya. Stok langsung terbuka kembali.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-tx-no">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-tx-yes" onClick={doDelete} className="bg-danger hover:bg-danger/90">
              Ya, Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
